import React, { useState, useCallback } from 'react';
import { Button, Card, message } from 'antd';
import {
  ReloadOutlined,
  UploadOutlined,
  PlusOutlined,
  AppstoreOutlined,
} from '@ant-design/icons';
import { useMerchantMenu } from '../../hooks/useMerchantMenu';
import {
  DishTable,
  AdminNoteModal,
  AddDishModal,
  AddCategoryModal,
} from '../merchant/menu';
import type { PatchDishPayload } from '../../types/menu';
import type {
  CreateDishPayload,
  CreateCategoryPayload,
} from '../../hooks/useMerchantMenu';
import { formatVnDateTime } from '../../utils/vnDateTime';

interface MenuTabProps {
  merchantId: string | undefined;
  restaurantId?: number;
}

export const MenuTab: React.FC<MenuTabProps> = ({
  merchantId,
  restaurantId,
}) => {
  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    title: string;
    onConfirm: (note: string) => Promise<void>;
  }>({ open: false, title: '', onConfirm: () => Promise.resolve() });
  const [addDishModalOpen, setAddDishModalOpen] = useState(false);
  const [addCategoryModalOpen, setAddCategoryModalOpen] = useState(false);

  const {
    categories,
    dishes,
    loading,
    refetch,
    isRefetching,
    patchDish,
    patchBulk,
    createDish,
    createCategory,
    deleteDish,
  } = useMerchantMenu(merchantId, { restaurantId });

  const lastUpdated =
    dishes.length > 0
      ? dishes.reduce((max, d) => {
          const t = new Date(d.updatedAt).getTime();
          return t > max ? t : max;
        }, 0)
      : null;

  const onOpenAdminNoteModal = useCallback(
    (title: string, onConfirm: (note: string) => Promise<void>) => {
      setConfirmModal({ open: true, title, onConfirm });
    },
    []
  );

  const onConfirmWithNote = useCallback(
    (action: string, fn: (note: string) => Promise<void>) => {
      onOpenAdminNoteModal(`Confirm ${action}`, fn);
    },
    [onOpenAdminNoteModal]
  );

  const handleConfirmModalOk = async (note: string) => {
    await confirmModal.onConfirm(note);
  };

  const handlePatchDish = async (dishId: string, payload: PatchDishPayload) => {
    try {
      await patchDish.mutateAsync({ dishId, payload });
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } } };
      message.error(ax.response?.data?.message || 'Failed to update dish');
      throw err;
    }
  };

  const handlePatchBulk = async (payload: {
    dishIds: string[];
    patch: {
      status?: 'PUBLISHED' | 'HIDDEN';
      stockStatus?: 'IN_STOCK' | 'OUT_OF_STOCK';
      categoryId?: string | null;
      priceDelta?: number;
      pricePercent?: number;
    };
    adminNote: string;
  }) => {
    try {
      await patchBulk.mutateAsync(payload);
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } } };
      message.error(ax.response?.data?.message || 'Bulk update failed');
      throw err;
    }
  };

  const handleAddCategory = () => {
    setAddCategoryModalOpen(true);
  };

  const handleCreateCategory = async (payload: CreateCategoryPayload) => {
    try {
      await createCategory.mutateAsync(payload);
      message.success('Đã thêm danh mục');
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } } };
      message.error(ax.response?.data?.message || 'Thêm danh mục thất bại');
      throw err;
    }
  };

  const handleAddDish = () => {
    setAddDishModalOpen(true);
  };

  const handleCreateDish = async (payload: CreateDishPayload) => {
    try {
      await createDish.mutateAsync(payload);
      message.success('Đã thêm món ăn');
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } } };
      message.error(ax.response?.data?.message || 'Thêm món thất bại');
      throw err;
    }
  };

  const handleDuplicateDish = () => {
    message.info('Duplicate dish feature coming soon');
  };

  const handleDeleteDish = async (
    dish: { id: string; name: string },
    _adminNote: string
  ) => {
    try {
      await deleteDish.mutateAsync(dish.id);
      message.success('Đã xóa món ăn');
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } } };
      message.error(ax.response?.data?.message || 'Failed to delete dish');
      throw err;
    }
  };

  return (
    <div className="merchant-detail-menu-tab">
      {/* Top Header */}
      <div className="merchant-menu-header">
        <h3 className="merchant-menu-title">Menu Management</h3>
        <div className="merchant-menu-header-actions">
          <Button
            icon={<ReloadOutlined />}
            onClick={() => refetch()}
            loading={isRefetching}
          >
            Sync / Reload
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddDish}
          >
            Thêm món ăn
          </Button>
        </div>
      </div>
      <div className="merchant-menu-meta">
        {lastUpdated && (
          <span className="merchant-menu-last-updated">
            Last updated: {formatVnDateTime(lastUpdated)}
          </span>
        )}
        <span className="merchant-menu-hint">
          Admin support mode: all changes are logged
        </span>
      </div>

      {/* Layout: Dish table only */}
      <div className="merchant-menu-layout-single">
        {dishes.length === 0 && !loading ? (
          <Card className="merchant-menu-empty-card">
            <div className="merchant-menu-empty-state">
              <p>Chưa có món ăn.</p>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddDish}
              >
                Thêm món ăn
              </Button>
              <Button
                icon={<AppstoreOutlined />}
                onClick={handleAddCategory}
                style={{ marginLeft: 8 }}
              >
                Thêm danh mục
              </Button>
            </div>
          </Card>
        ) : (
          <DishTable
            dishes={dishes}
            categories={categories}
            loading={loading}
            selectedCategoryId="__all__"
            onPatchDish={handlePatchDish}
            onPatchBulk={handlePatchBulk}
            onDuplicateDish={handleDuplicateDish}
            onDeleteDish={handleDeleteDish}
            onConfirmWithNote={onConfirmWithNote}
            onOpenAdminNoteModal={onOpenAdminNoteModal}
          />
        )}
      </div>

      <AdminNoteModal
        open={confirmModal.open}
        title={confirmModal.title}
        onConfirm={handleConfirmModalOk}
        onCancel={() => setConfirmModal(prev => ({ ...prev, open: false }))}
      />

      <AddDishModal
        open={addDishModalOpen}
        categories={categories}
        onSubmit={handleCreateDish}
        onCancel={() => setAddDishModalOpen(false)}
        loading={createDish.isPending}
      />

      <AddCategoryModal
        open={addCategoryModalOpen}
        onSubmit={handleCreateCategory}
        onCancel={() => setAddCategoryModalOpen(false)}
        loading={createCategory.isPending}
      />
    </div>
  );
};
